import { useEffect, useMemo, useState } from "react"
import {
  alpha,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material"
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded"
import CloseRounded from "@mui/icons-material/CloseRounded"
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded"
import LockOutlined from "@mui/icons-material/LockOutlined"
import { toast } from "react-toastify"

import ROUTE_PATHS from "src/routes/route-path"
import {
  IntegrationCatalogueDocument,
  useConnectIntegrationMutation,
  useStartIntegrationAuthorizationMutation
} from "src/graphql/quark/quark.type"
import { AUTH_FLOW_OAUTH, isSettingShown, SettingSpec } from "../connectorFeatures"
import ConnectorBadge from "./ConnectorBadge"
import SettingField from "./SettingField"

type CatalogueEntry = {
  connectorCode: string
  displayName: string
  description: string
  authFlow?: string | null
  settings: SettingSpec[]
}

const defaultValues = (specs: SettingSpec[]): Record<string, string> =>
  Object.fromEntries(
    specs.filter((spec) => spec.defaultValue != null).map((spec) => [spec.key, spec.defaultValue ?? ""])
  )

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="overline" color="text.secondary" fontWeight={700} letterSpacing="0.08em" display="block">
    {children}
  </Typography>
)

const Steps = ({ provider }: { provider: string }) => (
  <Box display="flex" alignItems="center" gap={1} mt={1.5}>
    {["Details", `Approve on ${provider}`].map((label, index) => (
      <Box key={label} display="flex" alignItems="center" gap={1}>
        {index > 0 && <Box sx={{ width: 24, height: 1.5, bgcolor: "divider", borderRadius: 1 }} />}
        <Box
          sx={(theme) => ({
            width: 20,
            height: 20,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            fontSize: 11,
            fontWeight: 700,
            color: index === 0 ? theme.palette.primary.contrastText : theme.palette.text.secondary,
            bgcolor: index === 0 ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.08)
          })}
        >
          {index + 1}
        </Box>
        <Typography
          variant="caption"
          fontSize="0.8rem"
          fontWeight={index === 0 ? 600 : 500}
          color={index === 0 ? "text.primary" : "text.secondary"}
        >
          {label}
        </Typography>
      </Box>
    ))}
  </Box>
)

const ConnectIntegrationModal = ({
  entry,
  onClose,
  onConnected
}: {
  entry: CatalogueEntry | null
  onClose: () => void
  onConnected: (connectionId: string) => void
}) => {
  const [name, setName] = useState("")
  const [environment, setEnvironment] = useState("sandbox")
  const [values, setValues] = useState<Record<string, string>>({})
  const [showAdvanced, setShowAdvanced] = useState(false)

  useEffect(() => {
    if (entry) setValues(defaultValues(entry.settings))
  }, [entry])

  const [connect, { loading }] = useConnectIntegrationMutation({
    refetchQueries: [{ query: IntegrationCatalogueDocument }]
  })
  const [startAuthorization, { loading: authorizing }] = useStartIntegrationAuthorizationMutation()

  const specs = useMemo(() => entry?.settings ?? [], [entry])
  const isOAuth = entry?.authFlow === AUTH_FLOW_OAUTH
  const shownSpecs = specs.filter((spec) => isSettingShown(spec, specs, values))

  // What it takes to connect goes up front; tuning that has a sensible default
  // waits under Advanced. A setting other settings depend on is never tucked away.
  const controllerKeys = new Set(specs.map((s) => s.visibleWhenKey).filter(Boolean))
  const isEssential = (spec: SettingSpec) => spec.required || spec.secret || controllerKeys.has(spec.key)
  const essentials = specs.filter((spec) => isEssential(spec) && !spec.visibleWhenKey)
  const dependentsOf = (key: string) => specs.filter((spec) => spec.visibleWhenKey === key)
  const advanced = specs.filter((spec) => !isEssential(spec) && !spec.visibleWhenKey)

  const setValue = (key: string) => (value: string) => setValues((prev) => ({ ...prev, [key]: value }))

  const handleClose = () => {
    setName("")
    setEnvironment("sandbox")
    setValues({})
    setShowAdvanced(false)
    onClose()
  }

  const handleConnect = async () => {
    if (!entry) return

    const settings = shownSpecs
      .filter((spec) => !spec.secret)
      .map((spec) => ({ key: spec.key, value: values[spec.key] ?? "" }))
    const credentials = shownSpecs
      .filter((spec) => spec.secret)
      .map((spec) => ({ key: spec.key, value: values[spec.key] ?? "" }))

    try {
      const result = await connect({
        variables: {
          input: {
            connectorCode: entry.connectorCode,
            displayName: name.trim() || entry.displayName,
            // A provider-authorised store has no sandbox of its own to choose.
            environment: isOAuth ? "production" : environment,
            settings,
            credentials
          }
        }
      })
      const id = result.data?.connectFusionIntegration.id
      if (id && isOAuth) {
        if (await redirectToAuthorization(id)) return
        // Created but not authorised: its page offers to finish connecting.
        handleClose()
        onConnected(id)
        return
      }
      toast.success(`${entry.displayName} connected`)
      handleClose()
      if (id) onConnected(id)
    } catch (error: any) {
      toast.error(error?.message ?? "Could not connect")
    }
  }

  const redirectToAuthorization = async (connectionId: string): Promise<boolean> => {
    try {
      const result = await startAuthorization({
        variables: {
          input: {
            connectionId,
            returnUrl: `${window.location.origin}${ROUTE_PATHS.INTEGRATIONS_HUB.BASE}/${connectionId}`
          }
        }
      })
      const authorizeUrl = result.data?.startFusionIntegrationAuthorization.authorizeUrl
      if (!authorizeUrl) return false
      window.location.assign(authorizeUrl)
      return true
    } catch (error) {
      toast.error(error instanceof Error ? error.message : `Could not open ${entry?.displayName} to approve access`)
      return false
    }
  }

  const theme = useTheme()
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"))

  const missingRequired = shownSpecs.some((spec) => spec.required && !(values[spec.key] ?? "").trim())
  const busy = loading || authorizing

  return (
    <Dialog
      open={!!entry}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      fullScreen={isPhone}
      PaperProps={{ sx: { borderRadius: isPhone ? 0 : 3, overflow: "hidden" } }}
    >
      <Box sx={{ px: 3, pt: 3, pb: 2.5, position: "relative" }}>
        <IconButton
          aria-label="Close"
          onClick={handleClose}
          size="small"
          sx={{ position: "absolute", top: 16, right: 16 }}
        >
          <CloseRounded fontSize="small" />
        </IconButton>
        <Box display="flex" alignItems="center" gap={1.75} pr={5}>
          {entry && <ConnectorBadge connectorCode={entry.connectorCode} displayName={entry.displayName} size={44} />}
          <Box minWidth={0}>
            <Typography variant="h6" fontWeight={700} lineHeight={1.25}>
              Connect {entry?.displayName}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.25}>
              {entry?.description}
            </Typography>
          </Box>
        </Box>
        {isOAuth && entry && <Steps provider={entry.displayName} />}
      </Box>

      <Divider />

      <DialogContent sx={{ px: 3, py: 3, display: "flex", flexDirection: "column", gap: 3 }}>
        <Box display="flex" flexDirection="column" gap={2.5}>
          <SectionTitle>Connection</SectionTitle>

          {essentials.map((spec) => {
            const dependents = dependentsOf(spec.key).filter((d) => isSettingShown(d, specs, values))
            return (
              <Box key={spec.key}>
                <SettingField
                  spec={spec}
                  variant="cards"
                  value={values[spec.key] ?? ""}
                  onChange={setValue(spec.key)}
                />
                <Collapse in={dependents.length > 0} unmountOnExit>
                  <Box
                    sx={(theme) => ({
                      mt: 1.5,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.06 : 0.03),
                      border: `1px solid ${theme.palette.divider}`,
                      display: "flex",
                      flexDirection: "column",
                      gap: 2
                    })}
                  >
                    {dependents.map((dependent) => (
                      <SettingField
                        key={dependent.key}
                        spec={dependent}
                        value={values[dependent.key] ?? ""}
                        onChange={setValue(dependent.key)}
                      />
                    ))}
                  </Box>
                </Collapse>
              </Box>
            )
          })}

          <Box>
            <Typography variant="body2" fontWeight={600}>
              Display name
            </Typography>
            <TextField
              fullWidth
              size="small"
              sx={{ mt: 1 }}
              placeholder={entry?.displayName}
              value={name}
              onChange={(e) => setName(e.target.value)}
              inputProps={{ "aria-label": "Display name" }}
            />
          </Box>

          {!isOAuth && (
            <Box>
              <Typography variant="body2" fontWeight={600}>
                Environment
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={environment}
                onChange={(_, next: string | null) => next && setEnvironment(next)}
                sx={{ mt: 1, display: "flex", "& .MuiToggleButton-root": { flex: 1, textTransform: "none" } }}
              >
                <ToggleButton value="sandbox">Sandbox</ToggleButton>
                <ToggleButton value="production">Production</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}
        </Box>

        {advanced.length > 0 && (
          <Box>
            <Button
              fullWidth
              color="inherit"
              onClick={() => setShowAdvanced((open) => !open)}
              endIcon={
                <ExpandMoreRounded
                  sx={{ transform: showAdvanced ? "rotate(180deg)" : "none", transition: "transform 0.2s ease" }}
                />
              }
              sx={(theme) => ({
                justifyContent: "space-between",
                textTransform: "none",
                px: 2,
                py: 1.25,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`
              })}
            >
              <Box textAlign="left">
                <Typography variant="body2" fontWeight={600}>
                  Advanced settings
                </Typography>
                <Typography variant="caption" fontSize="0.8rem" color="text.secondary">
                  Optional — sensible defaults apply. You can change these later.
                </Typography>
              </Box>
            </Button>
            <Collapse in={showAdvanced} unmountOnExit>
              <Box
                sx={{
                  pt: 2.5,
                  display: "grid",
                  gap: 2.5,
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  "& > .full": { gridColumn: "1 / -1" }
                }}
              >
                {advanced.map((spec) => (
                  <Box key={spec.key} className={spec.inputType === "number" ? undefined : "full"}>
                    <SettingField spec={spec} value={values[spec.key] ?? ""} onChange={setValue(spec.key)} />
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions
        disableSpacing
        sx={{
          px: 3,
          py: 2,
          gap: 1.5,
          justifyContent: "space-between",
          flexDirection: { xs: "column-reverse", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" }
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent={{ xs: "center", sm: "flex-start" }}
          gap={0.75}
          color="text.secondary"
          minWidth={0}
        >
          <LockOutlined sx={{ fontSize: 16 }} />
          <Typography variant="caption" fontSize="0.8rem">
            {isOAuth
              ? `You'll approve access on ${entry?.displayName} next.`
              : "You'll get a webhook URL for the provider's portal next."}
          </Typography>
        </Box>
        <Box display="flex" gap={1} flexDirection={{ xs: "column-reverse", sm: "row" }}>
          <Button color="inherit" onClick={handleClose} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            disabled={busy || missingRequired}
            onClick={() => void handleConnect()}
            endIcon={isOAuth ? <ArrowForwardRounded /> : undefined}
            sx={{ textTransform: "none", fontWeight: 600, px: 2.5, py: 1, borderRadius: 2, whiteSpace: "nowrap" }}
          >
            {busy ? "Connecting…" : isOAuth ? `Continue to ${entry?.displayName}` : "Connect"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}

export default ConnectIntegrationModal
